import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Autocomplete, Box, CircularProgress, TextField } from '@mui/material';
import { useInfiniteUsers, useUsers } from '../hooks';
import type { User } from '../api/types';

interface UserSelectSingleProps {
  value: string | null;
  onChange: (userId: string | null) => void;
  onUserChange?: (user: User | null) => void;
  label?: string;
  placeholder?: string;
  fullWidth?: boolean;
  // PEAK-269: narrow the picker to the same cohort the rest of the dashboard
  // is scoped to — without these, this always listed every user for the
  // client regardless of the active region/department/date-range/reliable-
  // data filters. Mirrors UserSelect (the multi-select variant)'s props.
  regionIds?: string[];
  depotIds?: string[];
  departmentIds?: string[];
  jobRoleIds?: string[];
  ageRanges?: string[];
  sex?: string[];
  shiftPatterns?: string[];
  startDate?: string;
  endDate?: string;
  reliableDataOnly?: boolean;
}

export const UserSelectSingle: React.FC<UserSelectSingleProps> = ({
  value,
  onChange,
  onUserChange,
  label = 'Select User',
  placeholder = 'Search and select a user...',
  fullWidth = true,
  regionIds,
  depotIds,
  departmentIds,
  jobRoleIds,
  ageRanges,
  sex,
  shiftPatterns,
  startDate,
  endDate,
  reliableDataOnly,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedUserData, setSelectedUserData] = useState<User | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteUsers({
    search: debouncedSearch,
    regionIds,
    depotIds,
    departmentIds,
    jobRoleIds,
    ageRanges,
    sex,
    shiftPatterns,
    startDate,
    endDate,
    reliableDataOnly,
  });

  const users = useMemo(() => data?.pages.flatMap((page) => page.data.users) ?? [], [data]);

  // A restored/URL-provided `value` won't be in the default (unsearched)
  // page of users unless it happens to sort near the top — fetch it by ID
  // directly so the Autocomplete shows the right label instead of blank.
  const needsSelectedUserLookup =
    Boolean(value) && selectedUserData?.id !== value && !users.some((u) => u.id === value);
  const { data: selectedUserLookupData } = useUsers(
    { user_ids: value ? [value] : [] },
    { enabled: needsSelectedUserLookup },
  );
  const lookedUpUser = useMemo(
    () => selectedUserLookupData?.data?.users?.find((u) => u.id === value) ?? null,
    [selectedUserLookupData, value],
  );

  const selectedUser = useMemo(() => {
    if (!value) return null;
    if (selectedUserData?.id === value) return selectedUserData;
    return users.find((u) => u.id === value) ?? lookedUpUser;
  }, [selectedUserData, users, value, lookedUpUser]);

  const options = useMemo(() => {
    const byName = (a: User, b: User) =>
      (a.name || a.email || '').localeCompare(b.name || b.email || '', undefined, {
        sensitivity: 'base',
      });
    const sorted = [...users].sort(byName);
    if (!selectedUserData) return sorted;
    // Keep the selected user pinned at the top, the rest sorted alphabetically.
    const rest = sorted.filter((u) => u.id !== selectedUserData.id);
    return [selectedUserData, ...rest];
  }, [selectedUserData, users]);

  const handleListboxScroll = (event: React.UIEvent<HTMLElement>) => {
    const el = event.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 48 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  useEffect(() => {
    if (!value) {
      setSelectedUserData(null);
      onUserChange?.(null);
      return;
    }
    if (selectedUserData?.id === value) return;
    const found = users.find((u) => u.id === value) ?? lookedUpUser;
    if (found) {
      setSelectedUserData(found);
      onUserChange?.(found);
    }
  }, [onUserChange, selectedUserData?.id, users, value, lookedUpUser]);

  if (error) {
    return <Alert severity='error'>Failed to load users. Please try again.</Alert>;
  }

  return (
    <Box>
      <Autocomplete
        fullWidth={fullWidth}
        options={options}
        value={selectedUser}
        onChange={(_event, newValue: User | null) => {
          setSelectedUserData(newValue);
          onUserChange?.(newValue);
          onChange(newValue?.id ?? null);
        }}
        inputValue={searchTerm}
        onInputChange={(_event, newInputValue, reason) => {
          // Clear search on focus to show all users
          if (reason === 'reset') {
            setSearchTerm('');
          } else {
            setSearchTerm(newInputValue);
          }
        }}
        onFocus={() => setSearchTerm('')}
        getOptionLabel={(option) => option.name || option.email}
        loading={isLoading || isFetchingNextPage}
        slotProps={{ listbox: { onScroll: handleListboxScroll } }}
        filterOptions={(x) => x}
        isOptionEqualToValue={(option, val) => option.id === val.id}
        blurOnSelect
        clearOnBlur
        openOnFocus
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            size='small'
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoading ? <CircularProgress color='inherit' size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
      />
    </Box>
  );
};
